import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, switchMap, timeout } from 'rxjs/operators';
import { EnvironmentAdapter } from 'src/app/adapters/environment/environment.adapter';
import { MSLatLng } from 'src/app/models/map.model';
import { Environment } from 'src/environments/environment.utils';
import { TypeTransformerUseCase } from '../../type-transformer/type-transformer.use-case';
import { BingMapsHttpUseCase } from '../http/bing-maps-http.use-case';
import {
  ReverseGeocodeEvent,
  mapLocationHttpErrorToReverseGeocodeErrorEvent,
  mapLocationHttpResponseToReverseGeocodeEvent
} from './utils/reverse-geocode.utils';

@Injectable({
  providedIn: 'root'
})
export class ReverseGeocodeUseCase {
  private environmentObservable = this.environmentAdapter.getEnvironment().pipe(shareReplay(1));

  constructor(
    private api: BingMapsHttpUseCase,
    private environmentAdapter: EnvironmentAdapter,
    private transformer: TypeTransformerUseCase
  ) {}

  public execute(point: MSLatLng): Observable<ReverseGeocodeEvent.Spec> {
    const observable = new Observable((observer) => observer.next()).pipe(
      mergeMap(() => this.environmentObservable),
      switchMap((environment) => this.geocode(point, environment)),
      shareReplay(1)
    );

    return observable;
  }

  private geocode(point: MSLatLng, environment: Environment): Observable<ReverseGeocodeEvent.Spec> {
    const reverseGeocodeObservable = this.api
      .get(`Locations/${point.lat},${point.lng}`, {
        includeEntityTypes: 'Address,Neighborhood',
        includeNeighborhood: 1
      })
      .pipe(
        timeout(environment.distanceConfig.httpRequestTimeout),
        map((response) => mapLocationHttpResponseToReverseGeocodeEvent(this.transformer, point, response)),
        catchError((error) => {
          const errorEvent = mapLocationHttpErrorToReverseGeocodeErrorEvent(error);
          return of(errorEvent);
        })
      );

    return reverseGeocodeObservable;
  }
}
